/**
 * PreordersPage (§167) — pre-order / reservation system. The grower publishes "available
 * soon" items (a variety + expected ripe window, prefillable from ripening predictions),
 * takes reservations (manually or via the public storefront), and marks an item ready →
 * every reserver is emailed. Backend grafty-api §213. Auth-gated → build-verified.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CalendarClock, X, Pencil, Trash2, BellRing, ChevronDown, ChevronRight, Plus } from 'lucide-react'
import { preordersApi, varietiesApi, ripeningApi, type PreorderItem } from '../lib/api'
import { apiError } from '../lib/errors'

const STATUS_COLOR: Record<string, string> = { open: '#16a34a', ready: '#2563EB', closed: '#6B7280', cancelled: '#9CA3AF' }
const RES_COLOR: Record<string, string> = { pending: '#D97706', confirmed: '#2563EB', fulfilled: '#16a34a', cancelled: '#9CA3AF' }
const RES_STATUSES = ['pending', 'confirmed', 'fulfilled', 'cancelled']

const card: React.CSSProperties = { background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)' }
const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }
const btn = (bg: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', background: bg, color: '#fff', fontSize: 12.5, fontWeight: 600 })
const iconBtn: React.CSSProperties = { display: 'inline-flex', padding: '5px 8px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'var(--color-bg-card)', color: 'var(--color-text)', cursor: 'pointer', flexShrink: 0 }
const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s
const fmtWindow = (a?: string, b?: string) => {
  if (!a && !b) return 'date TBD'
  const d = (s?: string) => s ? new Date(s + 'T12:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' }) : '?'
  return a && b ? `${d(a)} – ${d(b)}` : d(a || b)
}

export default function PreordersPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<PreorderItem | null>(null)
  const [creating, setCreating] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [msg, setMsg] = useState('')

  const { data, isLoading } = useQuery({ queryKey: ['preorders'], queryFn: () => preordersApi.list().then(r => r.data.items) })
  const items = data ?? []
  const { data: quota } = useQuery({ queryKey: ['preorder-quota'], queryFn: () => preordersApi.quota().then(r => r.data) })

  const remove = useMutation({
    mutationFn: (id: string) => preordersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['preorders'] }),
  })
  const ready = useMutation({
    mutationFn: (id: string) => preordersApi.markReady(id),
    onSuccess: (r) => { setMsg(`Marked ready — notified ${r.data.notified} reserver${r.data.notified !== 1 ? 's' : ''}.`); qc.invalidateQueries({ queryKey: ['preorders'] }) },
    onError: (e) => setMsg(apiError(e, 'Failed')),
  })

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarClock size={20} /> Pre-orders
        </h1>
        <button onClick={() => setCreating(true)} style={btn('var(--color-primary)')}>+ New pre-order</button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 14 }}>
        Let customers reserve fruit before it's ripe. Publish an "available soon" item with an
        expected window, take reservations, then mark it ready — everyone who reserved gets an email.
      </p>
      {msg && <div style={{ ...card, padding: '8px 12px', marginBottom: 12, fontSize: 12.5, color: 'var(--color-text)' }}>{msg}</div>}
      {quota && quota.monthly_limit > 0 && (() => {
        const used = quota.monthly_used, lim = quota.monthly_limit, rem = quota.remaining ?? Math.max(0, lim - used)
        const pct = Math.min(100, Math.round((used / lim) * 100))
        const over = rem <= 0, near = !over && rem <= Math.max(1, Math.ceil(lim * 0.15))
        const barColor = over ? '#dc2626' : near ? '#D97706' : 'var(--color-primary)'
        return (
          <div style={{ ...card, padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 6 }}>
              <span style={{ color: 'var(--color-text)', fontWeight: 600 }}>Storefront reservations this month</span>
              <span style={{ color: 'var(--color-text-muted)' }}>{used} / {lim}</span>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: 'var(--color-bg)', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: barColor }} />
            </div>
            {(over || near) && (
              <p style={{ fontSize: 11.5, color: over ? '#dc2626' : '#D97706', marginTop: 8 }}>
                {over
                  ? 'Your plan’s monthly storefront pre-order limit is reached — new public reservations are paused until next month. Upgrade your plan to accept more (you can still add reservations manually).'
                  : `Only ${rem} storefront reservation${rem !== 1 ? 's' : ''} left this month on your plan.`}
                {' '}<a href="/settings" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>Upgrade</a>
              </p>
            )}
          </div>
        )
      })()}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {isLoading ? (
          <div style={card}><p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>Loading…</p></div>
        ) : items.length === 0 ? (
          <div style={card}><p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>No pre-orders yet. Create one to start taking reservations for an upcoming harvest.</p></div>
        ) : items.map(it => (
          <div key={it.id} style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={() => setExpanded(expanded === it.id ? null : it.id)} style={{ ...iconBtn, border: 'none', background: 'none' }}>
                {expanded === it.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>
                  {it.name}
                  <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, color: '#fff', background: STATUS_COLOR[it.status] }}>{cap(it.status)}</span>
                  {it.is_published && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, color: 'var(--color-primary)', background: 'color-mix(in srgb, var(--color-primary) 16%, transparent)' }}>Published</span>}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', marginTop: 2 }}>
                  {[it.variety_name, fmtWindow(it.expected_from, it.expected_to)].filter(Boolean).join(' · ')}
                  {it.price != null && <span> · ${it.price.toFixed(2)}/{it.unit_label}</span>}
                  {it.min_quantity > 1 && <span> · min {it.min_quantity} {it.unit_label}s</span>}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)' }}>
                  {it.reserved}{it.capacity > 0 ? ` / ${it.capacity}` : ''} <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-faint)' }}>{it.unit_label}{it.reserved !== 1 ? 's' : ''}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-faint)' }}>{it.reservation_count} reservation{it.reservation_count !== 1 ? 's' : ''}</div>
              </div>
              {it.status === 'open' && (
                <button onClick={() => { if (confirm(`Mark "${it.name}" ready and email ${it.reservation_count} reserver(s)?`)) ready.mutate(it.id) }} title="Mark ready + notify" style={iconBtn}><BellRing size={15} /></button>
              )}
              <button onClick={() => setEditing(it)} title="Edit" style={iconBtn}><Pencil size={15} /></button>
              <button onClick={() => { if (confirm(`Delete "${it.name}" and its reservations?`)) remove.mutate(it.id) }} title="Delete" style={{ ...iconBtn, color: '#dc2626' }}><Trash2 size={15} /></button>
            </div>
            {expanded === it.id && <ReservationsPanel item={it} />}
          </div>
        ))}
      </div>

      {(creating || editing) && <ItemModal item={editing} onClose={() => { setCreating(false); setEditing(null) }} />}
    </main>
  )
}

function ReservationsPanel({ item }: { item: PreorderItem }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const { data } = useQuery({ queryKey: ['preorder-res', item.id], queryFn: () => preordersApi.reservations(item.id).then(r => r.data.reservations) })
  const reservations = data ?? []
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => preordersApi.setReservationStatus(id, status),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['preorder-res', item.id] }); qc.invalidateQueries({ queryKey: ['preorders'] }) },
  })
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-border-tertiary)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--color-text-muted)' }}>RESERVATIONS</span>
        <button onClick={() => setAdding(true)} style={{ ...btn('transparent'), color: 'var(--color-text)', border: '1px solid var(--color-border)', padding: '4px 10px' }}><Plus size={13} /> Add</button>
      </div>
      {reservations.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--color-text-faint)' }}>No reservations yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {reservations.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--color-bg)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{r.customer_name || 'Customer'} <span style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--color-text-muted)' }}>· {r.quantity} {item.unit_label}{r.quantity !== 1 ? 's' : ''}</span></div>
                <div style={{ fontSize: 11, color: 'var(--color-text-faint)' }}>{[r.customer_email, r.customer_phone].filter(Boolean).join(' · ') || '—'}{r.source === 'storefront' ? ' · storefront' : ''}{r.notified_at ? ' · notified' : ''}</div>
              </div>
              <select value={r.status} onChange={e => setStatus.mutate({ id: r.id, status: e.target.value })}
                style={{ ...inp, width: 'auto', fontSize: 11.5, padding: '4px 8px', color: RES_COLOR[r.status], fontWeight: 600 }}>
                {RES_STATUSES.map(s => <option key={s} value={s} style={{ color: 'var(--color-text)' }}>{cap(s)}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
      {adding && <AddReservationModal item={item} onClose={() => setAdding(false)} />}
    </div>
  )
}

function AddReservationModal({ item, onClose }: { item: PreorderItem; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', quantity: String(Math.max(1, item.min_quantity)), notes: '' })
  const [error, setError] = useState('')
  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }))
  const save = useMutation({
    mutationFn: () => preordersApi.addReservation({ item_id: item.id, customer_name: form.customer_name.trim(), customer_email: form.customer_email.trim(), customer_phone: form.customer_phone.trim(), quantity: Math.max(1, parseInt(form.quantity) || 1), notes: form.notes.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['preorder-res', item.id] }); qc.invalidateQueries({ queryKey: ['preorders'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Could not add reservation')),
  })
  return (
    <Overlay onClose={onClose}>
      <div style={{ ...card, width: 'min(440px, 94vw)' }}>
        <Header title={`Reserve · ${item.name}`} onClose={onClose} />
        <Field label="Customer name"><input value={form.customer_name} onChange={e => set('customer_name', e.target.value)} style={inp} /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
          <Field label="Email"><input value={form.customer_email} onChange={e => set('customer_email', e.target.value)} placeholder="for the ready alert" style={inp} /></Field>
          <Field label="Phone"><input value={form.customer_phone} onChange={e => set('customer_phone', e.target.value)} style={inp} /></Field>
        </div>
        <div style={{ marginTop: 10, maxWidth: 140 }}><Field label={`Quantity (${item.unit_label})`}><input type="number" min={1} value={form.quantity} onChange={e => set('quantity', e.target.value)} style={inp} /></Field></div>
        <div style={{ marginTop: 10 }}><Field label="Notes"><input value={form.notes} onChange={e => set('notes', e.target.value)} style={inp} /></Field></div>
        {error && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 10 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ ...btn('transparent'), color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>Cancel</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !form.customer_name.trim()} style={btn('var(--color-primary)')}>{save.isPending ? 'Saving…' : 'Reserve'}</button>
        </div>
      </div>
    </Overlay>
  )
}

function ItemModal({ item, onClose }: { item: PreorderItem | null; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    name: item?.name ?? '', variety_id: item?.variety_id ?? '', description: item?.description ?? '',
    unit_label: item?.unit_label ?? 'box', price: item?.price?.toString() ?? '',
    expected_from: item?.expected_from ?? '', expected_to: item?.expected_to ?? '',
    capacity: item?.capacity?.toString() ?? '0', min_quantity: item?.min_quantity?.toString() ?? '1',
    status: item?.status ?? 'open',
    is_published: item?.is_published ?? false, notes: item?.notes ?? '',
  })
  const [error, setError] = useState('')
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm(f => ({ ...f, [k]: v }))

  const { data: vars } = useQuery({ queryKey: ['varieties'], queryFn: () => varietiesApi.list().then(r => r.data.varieties) })
  const { data: preds } = useQuery({ queryKey: ['ripening-predictions'], queryFn: () => ripeningApi.predictions().then(r => r.data.predictions) })

  function prefill(p: { variety_name: string; predicted_start?: string; predicted_end?: string }) {
    const v = (vars ?? []).find(x => x.name.toLowerCase() === p.variety_name.toLowerCase())
    setForm(f => ({
      ...f,
      name: f.name || (p.variety_name ? `${p.variety_name} (pre-order)` : f.name),
      variety_id: v?.id ?? f.variety_id,
      expected_from: p.predicted_start ? p.predicted_start.slice(0, 10) : f.expected_from,
      expected_to: p.predicted_end ? p.predicted_end.slice(0, 10) : f.expected_to,
    }))
  }

  const save = useMutation({
    mutationFn: () => {
      const payload: Partial<PreorderItem> = {
        name: form.name.trim(), variety_id: form.variety_id || undefined, description: form.description.trim(),
        unit_label: form.unit_label.trim() || 'unit', price: form.price ? parseFloat(form.price) : undefined,
        expected_from: form.expected_from || undefined, expected_to: form.expected_to || undefined,
        capacity: parseInt(form.capacity) || 0, min_quantity: Math.max(1, parseInt(form.min_quantity) || 1),
        status: form.status, is_published: form.is_published, notes: form.notes.trim(),
      }
      return item ? preordersApi.update(item.id, payload) : preordersApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['preorders'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed to save')),
  })

  const upcoming = (preds ?? []).filter(p => p.predicted_peak).slice(0, 6)

  return (
    <Overlay onClose={onClose}>
      <div style={{ ...card, width: 'min(560px, 94vw)', maxHeight: '90vh', overflow: 'auto' }}>
        <Header title={item ? 'Edit pre-order' : 'New pre-order'} onClose={onClose} />
        {!item && upcoming.length > 0 && (
          <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: 'color-mix(in srgb, var(--color-primary) 5%, transparent)', border: '1px solid color-mix(in srgb, var(--color-primary) 14%, transparent)' }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 6 }}>PREFILL FROM A RIPENING PREDICTION</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {upcoming.map(p => (
                <button key={p.plant_id} onClick={() => prefill(p)} style={{ ...btn('transparent'), color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 11.5, padding: '4px 9px' }}>
                  {p.variety_name || p.plant_label} · {fmtWindow(p.predicted_start, p.predicted_end)}
                </button>
              ))}
            </div>
          </div>
        )}
        <Field label="Name"><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Glenn mangoes — pre-order" style={inp} /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
          <Field label="Variety"><select value={form.variety_id} onChange={e => set('variety_id', e.target.value)} style={inp}><option value="">— none —</option>{(vars ?? []).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></Field>
          <Field label="Unit label"><input value={form.unit_label} onChange={e => set('unit_label', e.target.value)} placeholder="box / flat / lb" style={inp} /></Field>
          <Field label="Available from"><input type="date" value={form.expected_from} onChange={e => set('expected_from', e.target.value)} style={inp} /></Field>
          <Field label="Available to"><input type="date" value={form.expected_to} onChange={e => set('expected_to', e.target.value)} style={inp} /></Field>
          <Field label={`Price per ${form.unit_label || 'unit'} ($)`}><input type="number" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} placeholder="optional" style={inp} /></Field>
          <Field label="Capacity (0 = unlimited)"><input type="number" min={0} value={form.capacity} onChange={e => set('capacity', e.target.value)} style={inp} /></Field>
          <Field label={`Minimum order (${form.unit_label || 'unit'}s)`}><input type="number" min={1} value={form.min_quantity} onChange={e => set('min_quantity', e.target.value)} style={inp} /></Field>
          <Field label="Status"><select value={form.status} onChange={e => set('status', e.target.value as PreorderItem['status'])} style={inp}>{['open', 'ready', 'closed', 'cancelled'].map(s => <option key={s} value={s}>{cap(s)}</option>)}</select></Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text)', alignSelf: 'end', paddingBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_published} onChange={e => set('is_published', e.target.checked)} /> Publish on storefront
          </label>
        </div>
        <div style={{ marginTop: 10 }}><Field label="Description (shown to customers)"><textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical' }} /></Field></div>
        <div style={{ marginTop: 10 }}><Field label="Pickup / fulfillment notes (used in the ready email)"><textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="Pick up at the farm stand Sat–Sun 9–2." style={{ ...inp, resize: 'vertical' }} /></Field></div>
        {error && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 10 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ ...btn('transparent'), color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>Cancel</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !form.name.trim()} style={btn('var(--color-primary)')}>{save.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Overlay>
  )
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      {children}
    </div>
  )
}
function Header({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>{title}</h2>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={18} /></button>
    </div>
  )
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block' }}><span style={lbl}>{label}</span>{children}</label>
}
